// frontend/src/components/onboarding/onboardingUtils.ts
// Shared labels, formatters and colour mapping for the onboarding module.
//
// The confidence colours are the Excel code that proved itself in the field:
// green = confirmed and used everywhere, orange = estimated and flagged
// wherever it influences a cost, red = missing and blocking.

import type {
  ConfidenceLevel, SourceDocumentKind, SourceDocumentStatus, DraftEntityType,
  DraftStatus, MatchMethod, AuditSeverity, OnboardingStage, ExtractionDraft, NormalizationIssue,
} from '../../types/onboarding';

export const CONFIDENCE_LABEL: Record<ConfidenceLevel, string> = {
  CONFIRMED: 'Confirmé',
  ESTIMATED: 'Estimé',
  MISSING: 'Manquant',
};

export const CONFIDENCE_HELP: Record<ConfidenceLevel, string> = {
  CONFIRMED: 'Vient d’une facture validée ou d’une saisie humaine. Utilisé dans tous les calculs.',
  ESTIMATED: 'Vient d’un catalogue, d’une extrapolation ou d’un ancien prix. Utilisé, mais signalé partout où il influence un cost.',
  MISSING: 'Non extrait, illisible ou orphelin. Bloque le calcul du cost de la recette concernée.',
};

export const CONFIDENCE_BADGE: Record<ConfidenceLevel, 'success' | 'warning' | 'danger'> = {
  CONFIRMED: 'success',
  ESTIMATED: 'warning',
  MISSING: 'danger',
};

/** Token trio per confidence level: text, background, border. */
export const CONFIDENCE_TOKENS: Record<ConfidenceLevel, { text: string; bg: string; border: string }> = {
  CONFIRMED: { text: 'var(--color-olive)', bg: 'var(--color-olive-bg)', border: 'var(--color-olive-border)' },
  ESTIMATED: { text: 'var(--color-amber)', bg: 'var(--color-amber-bg)', border: 'var(--color-amber-border)' },
  MISSING: { text: 'var(--color-brick)', bg: 'var(--color-brick-bg)', border: 'var(--color-brick-border)' },
};

export const DOCUMENT_KIND_LABEL: Record<SourceDocumentKind, string> = {
  INVOICE: 'Facture',
  CATALOG: 'Catalogue / portail',
  MENU: 'Menu',
  RECIPE: 'Recette',
  UNKNOWN: 'À préciser',
};

/**
 * Processing order is imposed: the product referential is built from invoices
 * before anything else, because a recipe cannot be linked to a product that
 * does not exist yet.
 */
export const DOCUMENT_KIND_ORDER: SourceDocumentKind[] = ['INVOICE', 'CATALOG', 'MENU', 'RECIPE', 'UNKNOWN'];

export const DOCUMENT_KIND_HELP: Record<SourceDocumentKind, string> = {
  INVOICE: 'Prix réellement payés. Traité en premier : c’est le référentiel produits.',
  CATALOG: 'Prix affichés. Toujours « estimés » tant qu’une facture ne les confirme pas.',
  MENU: 'La liste de ce qui est vendu — pas la vérité tarifaire.',
  RECIPE: 'Traité en dernier : chaque ligne doit pointer vers un produit existant.',
  UNKNOWN: 'Type non déterminé. Précisez-le pour lancer l’extraction.',
};

export const DOCUMENT_STATUS_LABEL: Record<SourceDocumentStatus, string> = {
  PENDING: 'En attente',
  CLASSIFIED: 'Classé',
  EXTRACTING: 'Extraction…',
  EXTRACTED: 'Extrait',
  FAILED: 'Échec',
  VALIDATED: 'Validé',
};

export const ENTITY_TYPE_LABEL: Record<DraftEntityType, string> = {
  SUPPLIER: 'Fournisseur',
  SUPPLIER_PRODUCT: 'Produit fournisseur',
  MENU_ITEM: 'Item du menu',
  RECIPE: 'Recette',
  POS_ITEM: 'Item de caisse',
  PARAMETER: 'Paramètre',
};

export const DRAFT_STATUS_LABEL: Record<DraftStatus, string> = {
  PENDING: 'À valider',
  ACCEPTED: 'Accepté',
  REJECTED: 'Rejeté',
  COMMITTED: 'Écrit',
};

export const MATCH_METHOD_LABEL: Record<MatchMethod, string> = {
  UPC: 'UPC exact',
  SUPPLIER_CODE: 'Code fournisseur',
  ALIAS: 'Alias déjà validé',
  BRAND_FORMAT: 'Marque + format',
  FUZZY: 'Similarité de nom',
  AI_SUGGESTED: 'Proposé par l’IA',
  NONE: 'Nouveau produit',
};

/** Méthodes qui passent TOUJOURS par l'humain (méthodologie §6). */
export const NON_AUTO_METHODS: ReadonlySet<MatchMethod> = new Set<MatchMethod>(['FUZZY', 'BRAND_FORMAT', 'AI_SUGGESTED']);

/** Raisons de mise en attente posées par la cascade / la passe catégorie. */
export const HOLD_REASON_LABEL: Record<string, string> = {
  MATCH_TO_VERIFY: 'Correspondance à vérifier',
  FORMAT_AMBIGUOUS: 'Format ambigu',
  MATCH_AMBIGUOUS: 'Plusieurs candidats',
  CATEGORY_AI_GUESSED: 'Catégorie devinée',
  FORMAT_DIFFERS_FROM_SHARED_BANK: 'Format différent de la banque commune',
};

/** Pourquoi ce brouillon attend un humain — en clair, sans doublon. */
export function holdReasons(draft: Pick<ExtractionDraft, 'issues' | 'matchMethod' | 'matchedResourceId' | 'matchDecidedByHuman'>): string[] {
  const reasons: string[] = [];
  for (const issue of draft.issues ?? []) {
    const label = HOLD_REASON_LABEL[issue.code];
    if (label && !reasons.includes(label)) reasons.push(label);
  }
  if (!draft.matchDecidedByHuman && draft.matchedResourceId && NON_AUTO_METHODS.has(draft.matchMethod)
    && !reasons.includes(HOLD_REASON_LABEL.MATCH_TO_VERIFY!)) {
    reasons.push(HOLD_REASON_LABEL.MATCH_TO_VERIFY!);
  }
  return reasons;
}

/** Issues de correspondance / catégorie (celles qui expliquent l'attente). */
export function holdIssues(issues: NormalizationIssue[] | null | undefined): NormalizationIssue[] {
  return (issues ?? []).filter((i) => HOLD_REASON_LABEL[i.code] !== undefined);
}

/**
 * Miroir client de la règle serveur (rematch.isSafeProductDraft) : un produit
 * s'accepte sans humain si son prix est CONFIRMÉ, rien ne bloque ni ne
 * retient, et sa correspondance est une CLÉ (UPC, code, alias) ou a déjà été
 * tranchée par un humain. Un produit NEUF reste à l'humain (le serveur peut
 * en accepter davantage : il connaît les décisions déjà prises).
 */
export function isAutoAcceptable(draft: ExtractionDraft): boolean {
  if (draft.status !== 'PENDING' || draft.confidence !== 'CONFIRMED') return false;
  const issues = draft.issues ?? [];
  if (issues.some((i) => i.severity === 'blocking')) return false;
  if (draft.entityType !== 'SUPPLIER_PRODUCT') return true;
  if (issues.some((i) => HOLD_REASON_LABEL[i.code] !== undefined && i.code !== 'CATEGORY_AI_GUESSED')) return false;
  if (draft.matchDecidedByHuman) return true;
  if (!draft.matchedResourceId) return false;
  return !NON_AUTO_METHODS.has(draft.matchMethod);
}

/** « 24x341ml » → « 24 × 341 ml » ; clé vide → « format inconnu ». */
export function formatKeyLabel(key: string | null | undefined): string {
  if (!key) return 'format inconnu';
  const m = key.match(/^(\d+(?:\.\d+)?)?x(\d+(?:\.\d+)?)(ml|g|un)$/);
  if (!m) return key;
  const pack = m[1] ? `${m[1]} × ` : '';
  const size = Number(m[2]);
  const unit = m[3];
  if (unit === 'ml' && size >= 1000) return `${pack}${size / 1000} L`;
  if (unit === 'g' && size >= 1000) return `${pack}${size / 1000} kg`;
  return `${pack}${size} ${unit === 'un' ? 'un' : unit}`;
}

export function compatLabel(compat: 'same' | 'different' | 'unknown' | undefined): string {
  switch (compat) {
    case 'same': return 'même format';
    case 'different': return 'format différent';
    default: return 'format inconnu';
  }
}

export const SEVERITY_LABEL: Record<AuditSeverity, string> = {
  BLOCKING: 'Bloquant',
  WARNING: 'À traiter',
  INFO: 'Pour information',
};

export const SEVERITY_BADGE: Record<AuditSeverity, 'danger' | 'warning' | 'neutral'> = {
  BLOCKING: 'danger',
  WARNING: 'warning',
  INFO: 'neutral',
};

export interface StepDefinition {
  stage: OnboardingStage;
  label: string;
  shortLabel: string;
  description: string;
}

export const STEPS: StepDefinition[] = [
  {
    stage: 'UPLOAD',
    label: 'Déposer les documents',
    shortLabel: 'Documents',
    description:
      'Factures, captures de portail, photos du menu, fiches de recettes. Déposez tout en vrac — ' +
      'le module trie et traite dans le bon ordre.',
  },
  {
    stage: 'EXTRACTION',
    label: 'Lecture automatique',
    shortLabel: 'Lecture',
    description:
      'Chaque document est lu et converti en données structurées. Rien n’est écrit à cette étape : ' +
      'tout arrive dans la file de validation.',
  },
  {
    stage: 'VALIDATION',
    label: 'File de validation',
    shortLabel: 'Validation',
    description:
      'Source à gauche, valeur extraite modifiable à droite. Vous tranchez uniquement les cas ambigus ; ' +
      'chaque confirmation rend l’import suivant automatique.',
  },
  {
    stage: 'QUESTIONS',
    label: 'Questions à l’établissement',
    shortLabel: 'Questions',
    description:
      'Ce que les documents ne disent pas, regroupé et dédoublonné. On ne demande pas quarante fois ' +
      'ce que vaut une once.',
  },
  {
    stage: 'COMMIT',
    label: 'Écriture',
    shortLabel: 'Écriture',
    description: 'Les éléments acceptés deviennent des produits, des recettes et des items de caisse.',
  },
  {
    stage: 'AUDIT',
    label: 'Rapport d’exploitation',
    shortLabel: 'Rapport',
    description:
      'Les incohérences que le croisement des trois sources fait apparaître : trous de revenus, ' +
      'costs aberrants, ventes qui ne décrémentent rien.',
  },
];

export function stageIndex(stage: OnboardingStage): number {
  const index = STEPS.findIndex((s) => s.stage === stage);
  if (index >= 0) return index;
  return stage === 'DONE' ? STEPS.length - 1 : 0;
}

// =============================================================================
// Formatters
// =============================================================================

const currency = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' });

export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return currency.format(value);
}

/**
 * Unit prices are frequently fractions of a cent ($/ml). Two decimals would
 * print "0,00 $" for every liquid in the inventory, so significant digits win
 * below one cent.
 */
export function formatUnitPrice(value: number | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const formatted = value < 0.01
    ? `${value.toPrecision(3)} $`
    : currency.format(value);
  return unit ? `${formatted} / ${unit}` : formatted;
}

export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)} %`;
}

export function formatQuantity(value: number | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const formatted = value.toLocaleString('fr-CA', { maximumFractionDigits: 4 });
  return unit ? `${formatted} ${unit}` : formatted;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Units offered in the editor, grouped so a dimension mistake is visible. */
export const UNIT_OPTIONS: Array<{ group: string; units: Array<{ value: string; label: string }> }> = [
  {
    group: 'Volume',
    units: [
      { value: 'ml', label: 'ml — millilitre' },
      { value: 'cl', label: 'cl — centilitre' },
      { value: 'L', label: 'L — litre' },
      { value: 'oz', label: 'oz — once' },
    ],
  },
  {
    group: 'Masse',
    units: [
      { value: 'g', label: 'g — gramme' },
      { value: 'kg', label: 'kg — kilogramme' },
      { value: 'lb', label: 'lb — livre' },
    ],
  },
  {
    group: 'Dénombrable',
    units: [
      { value: 'unit', label: 'unité — à la pièce' },
      { value: 'bouteille', label: 'bouteille' },
      { value: 'canette', label: 'canette' },
      { value: 'sachet', label: 'sachet' },
      { value: 'portion', label: 'portion' },
    ],
  },
];

const VOLUME = new Set(['ml', 'cl', 'dl', 'l', 'oz']);
const WEIGHT = new Set(['g', 'kg', 'lb']);

/** Client-side mirror of the backend's dimension check, for instant feedback. */
export function unitDimension(unit: string | null | undefined): 'volume' | 'weight' | 'count' | null {
  if (!unit) return null;
  const folded = unit.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (VOLUME.has(folded)) return 'volume';
  if (WEIGHT.has(folded)) return 'weight';
  return 'count';
}

export function dimensionLabel(dimension: 'volume' | 'weight' | 'count' | null): string {
  switch (dimension) {
    case 'volume': return 'volume';
    case 'weight': return 'masse';
    case 'count': return 'dénombrable';
    default: return 'inconnue';
  }
}

/** Human label for a canonical unit: 'unit' reads « unité », measures stay as-is. */
export function unitLabel(unit: string | null | undefined): string {
  if (!unit) return '';
  const folded = unit.toLowerCase();
  return folded === 'unit' || folded === 'unite' ? 'unité' : unit;
}

// =============================================================================
// Live preview of the derived unit price
// =============================================================================

const TO_BASE: Record<string, number> = {
  ml: 1, cl: 10, dl: 100, l: 1000, oz: 29.5735,
  g: 1, kg: 1000, lb: 453.592,
};

export interface UnitCostPreview {
  normalizedUnitCost: number | null;
  costPerConsumptionUnit: number | null;
  divisor: number | null;
  baseUnit: string | null;
  /** Human sentence explaining the division, shown under the derived value. */
  explanation: string | null;
  blocking: string | null;
}

/**
 * Mirror of computeNormalizedUnitCost on the server, so an edit shows its
 * effect on the derived price immediately instead of after a round-trip.
 *
 * The server recomputes this at write time regardless — this is a preview,
 * never the value that gets stored.
 *
 * `ozInMl` : l'once de l'établissement (paramètre de session `oz_en_ml`),
 * à lire par l'appelant ; sans lui, la constante américaine (29,5735) sert de
 * repli, exactement comme côté serveur sans overrides.
 */
export function previewUnitCost(input: {
  packUnits: number | null;
  unitSize: number | null;
  unitSizeUom: string | null;
  packPrice: number | null;
  deduction: number | null;
  consumptionUnit: string | null;
}, ozInMl?: number | null): UnitCostPreview {
  const toBase = (unit: string): number => {
    const folded = unit.toLowerCase();
    if (folded === 'oz' && ozInMl !== null && ozInMl !== undefined && ozInMl > 0) return ozInMl;
    return TO_BASE[folded] ?? 1;
  };
  const empty: UnitCostPreview = {
    normalizedUnitCost: null, costPerConsumptionUnit: null, divisor: null,
    baseUnit: null, explanation: null, blocking: null,
  };

  if (!input.consumptionUnit) {
    return { ...empty, blocking: 'Unité de consommation obligatoire : sans elle, aucun prix unitaire n’est calculable.' };
  }
  if (input.packPrice === null || !(input.packPrice > 0)) {
    return { ...empty, blocking: 'Prix du format manquant.' };
  }
  if (input.packUnits === null || !(input.packUnits > 0)) {
    return { ...empty, blocking: 'Nombre d’unités par format manquant.' };
  }

  const net = input.packPrice - (input.deduction ?? 0);
  if (net <= 0) {
    return { ...empty, blocking: 'La déduction dépasse le prix du format.' };
  }

  const consumptionDim = unitDimension(input.consumptionUnit);

  // « 0,341 unité » n'existe pas : l'unité de mesure a été remplacée par le
  // mot du contenant (BOTTLE, CAN). Même refus que le serveur.
  const sizeIsCount = unitDimension(input.unitSizeUom) === 'count';
  if (sizeIsCount && input.unitSize !== null && Number.isFinite(input.unitSize) && !Number.isInteger(input.unitSize)) {
    return {
      ...empty,
      blocking:
        `« ${input.unitSize} unité » n’a pas de sens : une contenance dénombrable est un entier. ` +
        'L’unité de mesure (ml / L / g) a probablement été remplacée par le mot du contenant — corrigez l’unité du format.',
    };
  }

  if (consumptionDim === 'count') {
    // Format dénombrable (« boîte de 12 » = 12 × 1 unité, ou l'ancienne forme
    // 1 × 12) : les pièces du format comptent dans le diviseur, comme le serveur.
    const piecesPerUnit = sizeIsCount && input.unitSize !== null && input.unitSize > 0 ? input.unitSize : 1;
    const divisor = input.packUnits * piecesPerUnit;
    const unitCost = net / divisor;
    return {
      normalizedUnitCost: unitCost,
      costPerConsumptionUnit: unitCost,
      divisor,
      baseUnit: 'unit',
      explanation: `${net.toFixed(2)} $ net ÷ ${divisor} ${unitLabel(input.consumptionUnit)}`,
      blocking: null,
    };
  }

  const formatDim = unitDimension(input.unitSizeUom);
  if (!input.unitSizeUom || !formatDim) {
    return { ...empty, blocking: 'Unité du format d’achat inconnue.' };
  }
  if (formatDim !== consumptionDim) {
    return {
      ...empty,
      blocking:
        `Incohérence de dimension : le format est en ${dimensionLabel(formatDim)} (${input.unitSizeUom}) ` +
        `mais la consommation est déclarée en ${input.consumptionUnit} (${dimensionLabel(consumptionDim)}). ` +
        (formatDim === 'count'
          ? 'Si ce produit se sert à la pièce, gardez la mesure (ml/g) comme « Unité du format » — la contenance est un volume ou un poids — et choisissez plutôt « unité » comme unité de consommation.'
          : 'Corrigez l’unité de consommation.'),
    };
  }
  if (input.unitSize === null || !(input.unitSize > 0)) {
    return { ...empty, blocking: 'Contenance par unité manquante.' };
  }

  const factor = toBase(input.unitSizeUom);
  const baseUnit = formatDim === 'volume' ? 'ml' : 'g';
  const divisor = input.packUnits * input.unitSize * factor;
  const unitCost = net / divisor;

  const consumptionFactor = toBase(input.consumptionUnit);

  return {
    normalizedUnitCost: unitCost,
    costPerConsumptionUnit: unitCost * consumptionFactor,
    divisor,
    baseUnit,
    explanation:
      `${net.toFixed(2)} $ net ÷ (${input.packUnits} × ${input.unitSize} ${input.unitSizeUom}) ` +
      `= ${divisor.toLocaleString('fr-CA', { maximumFractionDigits: 2 })} ${baseUnit}`,
    blocking: null,
  };
}
